import React, { useEffect, useMemo, useRef, useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { checkGrammar, getRecommendations, translate, VocabRecommendation } from '../utils/api';

const LANGUAGES = ['EN', 'ZH', 'AR', 'FR', 'DE', 'ES', 'RU', 'JA', 'KO'];
const TRENDING_WORDS = ['Ephemeral', 'Linguistic Drift', 'Pragmatics'];

type ToolMode = 'grammar' | 'translation';

function normalizeRecommendations(
  payload: VocabRecommendation[] | { items?: VocabRecommendation[]; recommendations?: VocabRecommendation[] }
) {
  if (Array.isArray(payload)) {
    return payload;
  }
  return payload.items || payload.recommendations || [];
}

export default function HomePage() {
  const navigate = useNavigate();
  const toolRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('EN');
  const [activeTool, setActiveTool] = useState<ToolMode>('grammar');
  const [toolInput, setToolInput] = useState('The nuance of a sentence often depends on context.');
  const [toolOutput, setToolOutput] = useState('');
  const [toolLoading, setToolLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<VocabRecommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [recommendationError, setRecommendationError] = useState('');

  useEffect(() => {
    let active = true;
    getRecommendations()
      .then((data) => {
        if (active) {
          setRecommendations(normalizeRecommendations(data));
        }
      })
      .catch((error) => {
        if (active) {
          setRecommendationError(error instanceof Error ? error.message : 'Unable to load recommendations.');
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRecommendations(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const wordOfDay = useMemo(
    () =>
      recommendations[0] || {
        word: 'Nuance',
        translation: '细微差别',
        definition: 'A subtle variation in meaning, expression, or response.',
        topic: 'Semantics',
        level: 'INTERMEDIATE',
        related_word: 'Context',
      },
    [recommendations]
  );

  const wordOfDayTranslation = useMemo(() => {
    if (Array.isArray(wordOfDay.translation)) {
      return wordOfDay.translation[0];
    }
    return wordOfDay.translation || wordOfDay.related_word || wordOfDay.topic || 'Concept in focus';
  }, [wordOfDay]);

  const handleAnalyze = (event: React.FormEvent) => {
    event.preventDefault();
    if (!query.trim()) {
      return;
    }
    navigate(`/word/${encodeURIComponent(query.trim())}?lang=${language.toLowerCase()}`);
  };

  const handleToolOpen = (mode: ToolMode) => {
    setActiveTool(mode);
    requestAnimationFrame(() => toolRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleToolSubmit = async () => {
    if (!toolInput.trim()) {
      return;
    }
    setToolLoading(true);
    try {
      if (activeTool === 'grammar') {
        const response = await checkGrammar(toolInput, language.toLowerCase());
        setToolOutput(response.corrected_text || response.corrections?.join(' ') || response.explanation || 'No changes suggested.');
      } else {
        const response = await translate(toolInput, language.toLowerCase());
        setToolOutput(response.translated_text || response.translation || 'No translation returned.');
      }
    } catch (error) {
      setToolOutput(error instanceof Error ? error.message : 'Unable to process your request right now.');
    } finally {
      setToolLoading(false);
    }
  };

  return (
    <Stack spacing={4.5}>
      <Card sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: '#f8fbff' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={4}>
            <Stack spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} textAlign={{ xs: 'left', md: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: { xs: '2.3rem', md: '3.8rem' } }}>
                Understand the <Box component="span" sx={{ color: '#0891b2', fontStyle: 'italic' }}>nuance</Box> of every word.
              </Typography>
              <Typography sx={{ maxWidth: 720, color: '#6b7280', fontSize: { xs: 16, md: 18 } }}>
                AI-powered analysis for meaning, grammar, and context across your study flow.
              </Typography>
            </Stack>

            <Box component="form" onSubmit={handleAnalyze}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{ p: 1.5, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}
              >
                <TextField
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Explore meanings, origins, or grammar..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField select value={language} onChange={(event) => setLanguage(event.target.value)} sx={{ minWidth: { xs: '100%', md: 120 } }}>
                  {LANGUAGES.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <Button type="submit" variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ px: 3.5, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
                  Analyze
                </Button>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {TRENDING_WORDS.map((word) => (
                <Chip key={word} label={word} onClick={() => setQuery(word)} sx={{ bgcolor: '#fff', border: '1px solid #dbeafe' }} />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Card sx={{ flex: 1, borderRadius: 3, borderLeft: '6px solid #00bcd4' }}>
          <CardActionArea onClick={() => handleToolOpen('grammar')}>
            <CardContent sx={{ p: 3.5 }}>
              <Stack spacing={2}>
                <Chip label="AI ADVANCED" sx={{ alignSelf: 'flex-start', bgcolor: '#e0f7fa', color: '#0f766e', fontWeight: 700 }} />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Grammar Correction</Typography>
                <Typography color="text.secondary">Context-aware proofreading that preserves your unique writing voice.</Typography>
                <Typography color="text.secondary">
                  <Box component="span" sx={{ textDecoration: 'line-through', mr: 1 }}>She do not understand the nuance.</Box>
                  <Box component="span" sx={{ color: '#16a34a', fontWeight: 700 }}>She does not understand the nuance.</Box>
                </Typography>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>

        <Card sx={{ flex: 1, borderRadius: 3 }}>
          <CardActionArea onClick={() => handleToolOpen('translation')}>
            <CardContent sx={{ p: 3.5 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TranslateRoundedIcon sx={{ color: '#1a73e8' }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>Translation</Typography>
                </Stack>
                <Typography color="text.secondary">Real-time semantic translation for 100+ languages.</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {['FR', 'DE', 'JP'].map((item) => (
                    <Chip key={item} label={item} sx={{ bgcolor: '#f1f5f9' }} />
                  ))}
                  <Chip label="+97 More" sx={{ bgcolor: '#111827', color: '#fff' }} />
                </Stack>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      </Stack>

      <Card ref={toolRef} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3.5 }}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {activeTool === 'grammar' ? 'Grammar Studio' : 'Translation Studio'}
              </Typography>
              <Chip label={activeTool === 'grammar' ? 'Grammar Correction' : 'Translation'} color="primary" />
            </Stack>
            <TextField
              multiline
              minRows={4}
              value={toolInput}
              onChange={(event) => setToolInput(event.target.value)}
              placeholder={activeTool === 'grammar' ? 'Paste a sentence to proofread...' : 'Enter text to translate...'}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" onClick={handleToolSubmit} disabled={toolLoading} sx={{ bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
                {toolLoading ? 'Analyzing...' : activeTool === 'grammar' ? 'Check grammar' : 'Translate text'}
              </Button>
              <Button variant="text" onClick={() => setToolOutput('')}>Clear output</Button>
            </Stack>
            {toolLoading && <LinearProgress />}
            {toolOutput && (
              <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ whiteSpace: 'pre-wrap', color: '#111827' }}>{toolOutput}</Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
        <Card sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent sx={{ p: 3.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Recommended for you</Typography>
              <Button endIcon={<ArrowForwardRoundedIcon />} sx={{ color: '#111827', fontWeight: 700 }}>VIEW LEARNING PATH</Button>
            </Stack>
            {loadingRecommendations ? (
              <Stack alignItems="center" py={6}><CircularProgress /></Stack>
            ) : recommendationError ? (
              <Alert severity="warning">{recommendationError}</Alert>
            ) : (
              <Stack spacing={2}>
                {recommendations.slice(0, 4).map((item) => (
                  <Card key={`${item.word}-${item.level}`} variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{item.word}</Typography>
                            <Chip label={item.level || 'INTERMEDIATE'} size="small" sx={{ bgcolor: '#e0f2fe', color: '#1d4ed8' }} />
                          </Stack>
                          <Typography color="text.secondary">{item.definition || item.reason || 'Build a deeper understanding through context and usage.'}</Typography>
                          <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={item.related_word || item.topic || item.domain || 'General'} />
                          </Stack>
                        </Box>
                        <IconButtonLike />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={{ width: { xs: '100%', xl: 320 }, borderRadius: 3, bgcolor: '#111827', color: '#fff' }}>
          <CardContent sx={{ p: 3.5, height: '100%' }}>
            <Stack spacing={2.5} justifyContent="space-between" height="100%">
              <Box>
                <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                  <TrendingUpRoundedIcon />
                  <Typography sx={{ letterSpacing: '0.08em', color: '#93c5fd' }}>Weekly Insight</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>+18%</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1.5 }}>
                  Your vocabulary growth accelerated this week across analysis, reading, and chat practice.
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 1 }}>Consistency score</Typography>
                <LinearProgress variant="determinate" value={76} sx={{ height: 10, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)', '& .MuiLinearProgress-bar': { bgcolor: '#22c55e' } }} />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3.5} justifyContent="space-between">
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Chip icon={<CalendarMonthRoundedIcon />} label="WORD OF THE DAY" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
              </Stack>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>{wordOfDay.word}</Typography>
              <Typography variant="h5" sx={{ color: '#0891b2', mb: 2 }}>{wordOfDayTranslation}</Typography>
              <Typography sx={{ fontStyle: 'italic', color: '#374151', mb: 1.5 }}>
                “A shift in tone can completely transform the nuance of a conversation.”
              </Typography>
              <Typography color="text.secondary">— DictZoom Editorial Notes</Typography>
              <Button sx={{ mt: 3, bgcolor: '#111827', color: '#fff', '&:hover': { bgcolor: '#1f2937' } }}>+ Add to Study List</Button>
            </Box>
            <Stack spacing={2.5} sx={{ width: { xs: '100%', lg: 320 } }}>
              <MetricCard label="7-day streak" value="12" subtext="days active" />
              <MetricCard label="Words mastered" value="48" subtext="+9% this week" />
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontWeight: 700 }}>Weekly goal</Typography>
                  <Typography color="text.secondary">78%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={78} sx={{ height: 10, borderRadius: 999, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: '#1a73e8' } }} />
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

function IconButtonLike() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: '50%', bgcolor: '#f8fafc' }}>
      <BookmarkBorderRoundedIcon sx={{ color: '#6b7280' }} />
    </Box>
  );
}

function MetricCard({ label, value, subtext }: { label: string; value: string; subtext: string }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
      <Typography color="text.secondary" sx={{ mb: 0.75 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>{value}</Typography>
      <Typography color="text.secondary">{subtext}</Typography>
    </Box>
  );
}
